import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, RotateCcw, Bot, User as UserIcon } from "lucide-react";

// Suggested opener prompts — reduces cold-start friction. Clicking submits.
const SUGGESTIONS = [
  "Which ETFs have the safest high dividends right now?",
  "Which dividend ETFs have historically outperformed the S&P 500?",
  "SCHD vs VYM vs DGRO — how do they differ?",
  "Best monthly-dividend payers for cash flow?",
];

// Persist chat locally so refresh doesn't wipe context.
const STORAGE_KEY = "dpp-research-chat";
const SESSION_KEY = "dpp-research-chat-session";

function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(-20) : [];
  } catch { return []; }
}
function saveHistory(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-20))); } catch { /* quota */ }
}
function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `s${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
    try { localStorage.setItem(SESSION_KEY, s); } catch { /* ignore */ }
  }
  return s;
}

// Very light markdown-ish renderer: **bold** + line breaks. Keeps bundle small
// and avoids pulling in a full markdown parser for a chat bubble.
function renderReply(text) {
  const nodes = [];
  const lines = String(text || "").split("\n");
  lines.forEach((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    nodes.push(
      <span key={i}>
        {parts.map((p, j) => p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>)}
      </span>
    );
    if (i < lines.length - 1) nodes.push(<br key={`br-${i}`} />);
  });
  return nodes;
}

export default function ResearchChatCard() {
  const [messages, setMessages] = useState(() => loadHistory());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { saveHistory(messages); }, [messages]);

  // Auto-scroll to the newest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || sending) return;
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/research/chat", {
        session_id: getSession(),
        message: text,
        history: messages.slice(-10),  // last 10 turns for context
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "AI is temporarily unavailable.";
      // If it's a limit error surface an actionable message; otherwise toast.
      if (e?.response?.status === 402) {
        setMessages((prev) => [...prev, { role: "assistant", content: `**Free-tier limit reached.** ${detail}` }]);
      } else {
        toast.error(detail);
      }
    } finally { setSending(false); }
  };

  const reset = () => {
    if (messages.length && !window.confirm("Clear the current conversation?")) return;
    setMessages([]);
    try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  };

  return (
    <Card className="border-secondary/30 bg-secondary/[0.02] flex flex-col h-full" data-testid="research-chat-card">
      <CardContent className="p-6 flex flex-col flex-1 min-h-[420px]">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> AI research assistant
            </div>
            <h3 className="serif text-xl mt-1">Ask anything about dividends.</h3>
          </div>
          {messages.length > 0 && (
            <button
              onClick={reset}
              className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="chat-reset"
              title="Clear conversation"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          )}
        </div>

        {/* Composer at top — quick access without scrolling */}
        <form
          className="flex items-center gap-2 mb-3"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={sending ? "Waiting for a reply…" : "Ask about high-yield ETFs, DRIP, safety, tax…"}
            disabled={sending}
            data-testid="chat-input"
            maxLength={2000}
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            className="rounded-full shrink-0"
            data-testid="chat-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>

        {/* Scrollable transcript */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto rounded-md border border-border bg-card px-3 py-3 space-y-3"
          data-testid="chat-transcript"
        >
          {messages.length === 0 ? (
            <div className="text-center py-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto mb-2">
                <Bot className="w-5 h-5" />
              </div>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">
                Educational only, not personalized advice. Try one of these to get started:
              </p>
              <div className="mt-3 grid gap-1.5">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    disabled={sending}
                    className="text-left text-xs rounded-md border border-border bg-background hover:border-primary/40 hover:bg-primary/5 transition-colors px-3 py-2 leading-relaxed"
                    data-testid={`chat-suggest-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} />)
          )}
          {sending && (
            <div className="flex items-start gap-2" data-testid="chat-thinking">
              <div className="w-7 h-7 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
                <Bot className="w-4 h-4" />
              </div>
              <div className="rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>

        <p className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-2 text-center">
          Powered by Claude · Educational only · Counts toward monthly AI budget
        </p>
      </CardContent>
    </Card>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex items-start gap-2 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${role}`}>
      <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${
        isUser ? "bg-secondary/20 text-secondary" : "bg-primary/15 text-primary"
      }`}>
        {isUser ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-4 h-4" />}
      </div>
      <div className={`rounded-md px-3 py-2 text-sm leading-relaxed max-w-[85%] ${
        isUser ? "bg-secondary/10 border border-secondary/20" : "bg-muted/40 border border-border"
      }`}>
        {renderReply(content)}
      </div>
    </div>
  );
}
